import { memo, type ReactNode, useEffect, useState } from 'react'

import { getDefaultLanguageFromUrl, useSetDefaultLanguage } from '@orus.eu/pharaoh'

import { useSession } from '../../lib/session.ts'
import { GlobalLoadingState } from '../molecules/global-loading-state.tsx'

/**
 * Applies the session's default language, resolved by the backend when the session was created — from
 * the url the visitor landed on, or from the country their IP is located in — so that someone landing
 * on the sign-up page from Spain or the Netherlands doesn't get a french funnel.
 *
 * A default, not a choice: a language asked for through the current url wins, and so does an explicit
 * choice, which the session carries separately and applies over any default.
 *
 * Renders the loading screen for the one frame it takes to apply the language, rather than letting a
 * screen paint in french and then switch under the eyes of whoever is already reading it. The language
 * comes with the session, so there is nothing to wait for here beyond that frame.
 */
export const SessionDefaultLanguageWrapper = memo<{ children: ReactNode }>(function SessionDefaultLanguageWrapper({
  children,
}) {
  const { defaultLanguage } = useSession()
  const setDefaultLanguage = useSetDefaultLanguage()

  const languageToApply = getDefaultLanguageFromUrl() ? undefined : defaultLanguage
  const [isApplied, setIsApplied] = useState(!languageToApply)

  useEffect(() => {
    if (!languageToApply) return
    setDefaultLanguage(languageToApply)
    setIsApplied(true)
  }, [languageToApply, setDefaultLanguage])

  if (!isApplied) return <GlobalLoadingState />

  return children
})
